import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  companyFromRow,
  toCompanyContact,
  toCompanySummary,
  toProjectSummary,
  toUserSummary,
} from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type {
  CompanyListItem as CompanyListItemDto,
  CompanyOverview,
  CompanyRead,
  ContactRead,
  Page,
  ProjectListItem,
  UserRead,
} from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { ensureUserIndex, useUserIndex } from '@/shared/hooks/use-users'
import type {
  CompanyContact,
  CompanyDetail,
  CompanyListItem,
  ProjectSummary,
} from '@/shared/types/domain'

/**
 * El listado de empresas.
 *
 * `GET /companies` devuelve **`Page[CompanyListItem]`**: la ficha más los dos
 * contadores que se leen de un vistazo (`active_projects`, `overdue_tasks`),
 * en la misma consulta. Antes esos dos números salían de una petición a
 * `/overview` **por empresa** —un N+1 para dibujar dos columnas—; ahora llegan
 * con la lista.
 *
 * Los **cuatro** contadores llegan con la lista. Los dos últimos —tareas
 * abiertas y contactos— se sumaron después: hasta entonces la tabla dibujaba un
 * guión en esas dos columnas para todas las empresas, siempre. El guión decía la
 * verdad, pero un dato que no llega no se arregla con una etiqueta bien escrita.
 *
 * El responsable se resuelve contra el índice de usuarios, que ya está
 * cacheado: el listado manda `primary_tech_owner_id`, no el objeto.
 */
export function useCompanies(filters: Record<string, string> = {}) {
  const search = filters.search?.toLowerCase().trim() ?? ''
  const client = useQueryClient()

  return useQuery({
    queryKey: qk.companies.list(filters),
    queryFn: async () => {
      const [users, page] = await Promise.all([
        ensureUserIndex(client),
        api.get<Page<CompanyListItemDto>>('companies?limit=200'),
      ])

      return page.items.map((company): CompanyListItem => ({
        ...toCompanySummary(company),
        primary_tech_owner: company.primary_tech_owner_id
          ? (users.get(company.primary_tech_owner_id) ?? null)
          : null,
        active_projects: company.active_projects,
        overdue_tasks: company.overdue_tasks,
        open_tasks: company.open_tasks,
        contacts_count: company.contacts_count,
      }))
    },
    select: (companies) =>
      search
        ? companies.filter((company) => company.name.toLowerCase().includes(search))
        : companies,
    staleTime: 60_000,
  })
}

/** El detalle sale de `/companies/{id}/overview`, que trae empresa + números. */
export function useCompany(companyId: string) {
  const users = useUserIndex()

  return useQuery({
    queryKey: qk.companies.detail(companyId),
    queryFn: () => api.get<CompanyOverview>(`companies/${companyId}/overview`),
    enabled: Boolean(companyId),
    select: (overview): CompanyDetail => ({
      ...toCompanySummary(overview.company),
      primary_tech_owner: overview.company.primary_tech_owner_id
        ? (users.get(overview.company.primary_tech_owner_id) ?? null)
        : null,
      active_projects: overview.open_projects,
      open_tasks: overview.open_tasks,
      overdue_tasks: overview.overdue_tasks,
      contacts_count: overview.contact_count,
      slack_channel: overview.company.slack_channel_id,
      notes_internal: overview.company.internal_notes_md,
      created_at: overview.company.created_at,
    }),
  })
}

export function useCompanyContacts(companyId: string) {
  return useQuery({
    queryKey: qk.companies.contacts(companyId),
    queryFn: async () => {
      const contacts = await api.get<ContactRead[]>(`companies/${companyId}/contacts`)
      return contacts.map(toCompanyContact)
    },
    enabled: Boolean(companyId),
  })
}

/**
 * Los proyectos de una empresa. **Todos**, no sólo los vivos.
 *
 * `GET /companies/{id}/projects` **no existe**: el filtro por empresa es un
 * query param del listado de proyectos, que además devuelve `Page`.
 *
 * `include_ongoing` e `include_done` van los dos en `true` porque los defaults
 * del endpoint son `false` y esta pestaña es el inventario de la cuenta, no un
 * tablero de lo que está en curso: acá es donde se contesta "¿qué les
 * entregamos este año?" en una reunión de renovación. Sin esto, lo entregado
 * sólo se alcanzaba escribiendo la URL del proyecto a mano (QA funcional 1.2).
 *
 * El portafolio es distinto y sigue siendo distinto: ahí sin filtros se
 * muestra lo vivo, y los culminados aparecen cuando se los pide por fase.
 */
export function useCompanyProjects(companyId: string) {
  const client = useQueryClient()

  return useQuery({
    queryKey: qk.companies.projects(companyId),
    queryFn: async (): Promise<ProjectSummary[]> => {
      const [users, page] = await Promise.all([
        ensureUserIndex(client),
        api.get<Page<ProjectListItem>>(
          `projects?company_id=${encodeURIComponent(companyId)}&limit=200` +
            `&include_ongoing=true&include_done=true`,
        ),
      ])
      return page.items.map((item) => toProjectSummary(item, users))
    },
    enabled: Boolean(companyId),
  })
}

/* ------------------------------------------------------------------ altas -- */

/**
 * Las altas que faltaban.
 *
 * El QA funcional cerró el recorrido "una empresa nueva, de punta a punta" con
 * una sola frase: **no se puede hacer, la mayor parte no tiene pantalla**. El
 * backend estaba entero —los siete pasos pasaban por `curl` sin un error— y lo
 * que faltaba era la mitad de la interfaz: onboardear una cuenta exigía abrir
 * una terminal.
 *
 * Nada de esto inventa reglas: cada mutación llama al endpoint que ya existía.
 */

export interface CompanyCreateInput {
  name: string
  slug: string
  primary_tech_owner_id?: string | null
  color_hue?: number | null
  slack_channel_id?: string | null
}

/**
 * Alta de empresa.
 *
 * Invalida `companies.lists()` y no `companies.list({})`: el listado se cachea
 * por filtros y la empresa nueva tiene que aparecer en todas las combinaciones,
 * no sólo en la que esté abierta en ese momento. El factory de query keys deja
 * los filtros en el último segmento justamente para que este prefijo alcance.
 */
export function useCreateCompany() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CompanyCreateInput) => api.post<CompanyRead>('companies', { ...input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.companies.lists() })
    },
  })
}

export interface CompanyUpdateInput {
  name?: string
  status?: 'active' | 'paused' | 'archived'
  primary_tech_owner_id?: string | null
  color_hue?: number
  slack_channel_id?: string | null
  internal_notes_md?: string | null
}

/**
 * Edita la ficha. **Parcial**: lo que no viaja, no se toca.
 *
 * Invalida la ficha Y los listados: el nombre, el estado y el hue de la
 * espina aparecen en el portafolio, en la línea de tiempo y en el conmutador de
 * empresas. Dejar viva la lista vieja mostraría el nombre anterior en cuatro
 * pantallas hasta el próximo refetch.
 */
export function useUpdateCompany(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CompanyUpdateInput) =>
      api.patch<CompanyRead>(`companies/${companyId}`, { ...input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.companies.detail(companyId) })
      void client.invalidateQueries({ queryKey: qk.companies.lists() })
      // El nombre y el hue viajan dentro de cada proyecto del portafolio.
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
    },
  })
}

/**
 * Archiva la empresa (borrado suave).
 *
 * El backend contesta **409 si le quedan proyectos vivos**, y eso no es un
 * obstáculo a esquivar: los proyectos de una cuenta que se va se cierran o se
 * mueven a otra empresa, y eso lo decide alguien. La pantalla muestra ese
 * mensaje tal cual viene.
 */
export function useArchiveCompany() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (companyId: string) => api.delete(`companies/${companyId}`),
    onSuccess: (_data, companyId) => {
      void client.invalidateQueries({ queryKey: qk.companies.lists() })
      void client.invalidateQueries({ queryKey: qk.companies.detail(companyId) })
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
    },
  })
}

export interface ContactCreateInput {
  name: string
  email: string
  role_title?: string | null
  is_primary?: boolean
  locale?: string
  notes_internal?: string | null
}

/** Alta de contacto. Nace **sin** login del portal: darlo es un acto aparte. */
export function useCreateContact(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: ContactCreateInput) =>
      api.post<ContactRead>(`companies/${companyId}/contacts`, { ...input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.companies.contacts(companyId) })
      // El contador de contactos vive en el overview, que es otra query.
      void client.invalidateQueries({ queryKey: qk.companies.detail(companyId) })
    },
  })
}

/**
 * Editar un contacto.
 *
 * Existe sobre todo por el **idioma**: el alta ya lo elige, pero si se
 * equivocaron no había pantalla para corregirlo y el contacto quedaba viendo su
 * portal en un idioma que no habla. El cliente no puede arreglarlo él mismo, y
 * eso es **deliberado**: el portal es read-only absoluto y hay un test de
 * contrato que exige que `/api/v1/portal` sólo registre GET. Si la promesa es
 * "el idioma es de la persona", alguien del equipo tiene que poder cambiarlo.
 *
 * Cambiar el idioma de un contacto **no** cambia el de su usuario de portal: el
 * `locale` del contacto es la semilla con la que ese usuario nace. Los dos se
 * editan por separado a propósito — una persona puede haber cambiado el suyo.
 */
export function useUpdateContact(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ contactId, ...patch }: ContactUpdateInput & { contactId: string }) =>
      api.patch<ContactRead>(`contacts/${contactId}`, { ...patch }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.companies.contacts(companyId) })
      void client.invalidateQueries({ queryKey: qk.companies.detail(companyId) })
    },
  })
}

export type ContactUpdateInput = Partial<ContactCreateInput>

/**
 * Dar acceso al portal a un contacto.
 *
 * **No lleva cuerpo**: el endpoint deriva todo del contacto (su email es el
 * login, su empresa es el alcance, su `locale` es el idioma de los avisos). Es
 * además el único camino por el que nace un usuario con rol `client`.
 *
 * Se separa del alta de contacto a propósito y no es una casilla del mismo
 * formulario: cargar a alguien en la agenda y **darle una cuenta que ve datos**
 * son dos decisiones distintas, y la segunda merece su propio clic.
 */
export function useGrantPortalAccess(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (contactId: string) =>
      api.post<ContactRead>(`contacts/${contactId}/grant-access`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.companies.contacts(companyId) })
    },
  })
}

/** Reexportado para los tests y para quien necesite la forma cruda. */
export type { CompanyContact, UserRead }
export { companyFromRow, toUserSummary }
